import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Bar, BarChart, CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  Empty,
  EmptyHeader,
  EmptyTitle,
  Skeleton,
  ToggleGroup,
  ToggleGroupItem,
  type ChartConfig,
} from '@monksflow/monks-ui';
import type { PerformanceDailyMetric } from '@/entities/media-campaign';
import { formatMetric } from '@/shared/utils/metric-format';

export type TrendMetric = 'impressions' | 'clicks' | 'conversions';

/**
 * The toggle's options, in order. A plain list of metric keys rather than a label table:
 * the labels have to resolve at render, and a module-scope table of `t(...)` results would
 * freeze them at import.
 */
const TREND_METRICS: readonly TrendMetric[] = ['impressions', 'clicks', 'conversions'];

function metricLabel(metric: TrendMetric, t: TFunction<'projects'>): string {
  switch (metric) {
    case 'impressions':
      return t('performanceDashboard.trendCharts.impressions');
    case 'clicks':
      return t('performanceDashboard.trendCharts.clicks');
    case 'conversions':
      return t('performanceDashboard.trendCharts.conversions');
  }
}

// Fixed pixel width on both y-axes, not the recharts default of auto-sizing to the longest
// tick label: spend and the delivery metric format to different digit counts, and an
// auto-sized axis would shift the plot area between the two charts, breaking the shared
// x-axis alignment this pair exists to give.
const Y_AXIS_WIDTH = 56;

/**
 * Fixed slots, not seriesVarForPlatform: spend and the delivery metric are two measures on
 * this one chart pair, not platforms, so a lookup keyed by platform identity has nothing to
 * key on. --series-1 and --series-2 are the validated adjacent pair that clears contrast in
 * both themes; --series-3 through --series-5 fall below the 3:1 floor a 2px line stroke
 * needs to stay legible (see global.css).
 */
const SPEND_COLOR = 'var(--series-1)';
const METRIC_COLOR = 'var(--series-2)';

export interface TrendChartsProps {
  data: PerformanceDailyMetric[];
  metric: TrendMetric;
  onMetricChange: (metric: TrendMetric) => void;
  currency: string | null;
  /**
   * Whether a day's spend is one currency's worth. A daily metric carries no currency of
   * its own — the series is keyed on the date alone — so across a project mixing them, a
   * bar is a EUR figure added to a USD one. False hides the spend chart rather than
   * plotting that, which is the same refusal the headline tile and `spendByCurrency`
   * already make on the same response.
   */
  isSingleCurrency: boolean;
  isLoading?: boolean;
}

/**
 * Spend and the selected delivery metric as two single-axis charts sharing an x-axis.
 *
 * Not one chart with spend and the metric on two y-scales: whoever sets those two scales
 * decides where the lines happen to cross, so the reader sees a correlation the data itself
 * does not contain. Giving each series its own axis removes that degree of freedom.
 */
export function TrendCharts({
  data,
  metric,
  onMetricChange,
  currency,
  isSingleCurrency,
  isLoading = false,
}: TrendChartsProps) {
  const { t } = useTranslation('projects');

  const spendConfig = {
    spend: { label: t('performanceDashboard.trendCharts.spend'), color: SPEND_COLOR },
  } satisfies ChartConfig;

  const metricConfig = {
    [metric]: { label: metricLabel(metric, t), color: METRIC_COLOR },
  } satisfies ChartConfig;

  return (
    <div className="flex flex-col gap-3">
      <ToggleGroup
        type="single"
        value={metric}
        onValueChange={(next) => {
          // The group clears its value when the active item is clicked again; ignore that
          // rather than switching the chart to no metric at all.
          if (next) onMetricChange(next as TrendMetric);
        }}
      >
        {TREND_METRICS.map((option) => (
          <ToggleGroupItem key={option} value={option}>
            {metricLabel(option, t)}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>

      {isLoading ? (
        <Skeleton className="h-64 w-full" data-testid="trend-chart-skeleton" />
      ) : data.length === 0 ? (
        <Empty>
          <EmptyHeader>
            <EmptyTitle>{t('performanceDashboard.trendCharts.noDeliveryInThisPeriod')}</EmptyTitle>
          </EmptyHeader>
        </Empty>
      ) : (
        <div className="flex flex-col gap-1">
          {!isSingleCurrency ? (
            <div data-testid="trend-chart-spend-mixed-currency">
              <p className="text-sm font-medium text-foreground">
                {t('performanceDashboard.trendCharts.spend')}
              </p>
              <p className="py-4 text-sm text-muted-foreground">
                {t('performanceDashboard.trendCharts.spendSpansCurrencies')}
              </p>
            </div>
          ) : (
            <div data-testid="trend-chart-spend">
              <p className="text-sm font-medium text-foreground">
                {currency
                  ? t('performanceDashboard.trendCharts.spendWithCurrency', { currency })
                  : t('performanceDashboard.trendCharts.spend')}
              </p>
              <ChartContainer config={spendConfig} className="h-32 w-full">
                <BarChart
                  data={data}
                  margin={{ top: 4, right: 4, left: 4, bottom: 0 }}
                  barCategoryGap={2}
                >
                  <CartesianGrid vertical={false} />
                  <XAxis dataKey="date" tickLine={false} axisLine={false} tick={false} height={4} />
                  <YAxis
                    width={Y_AXIS_WIDTH}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 11 }}
                  />
                  <ChartTooltip
                    content={
                      <ChartTooltipContent
                        formatter={(value) => formatMetric(Number(value), 'currency', currency)}
                      />
                    }
                  />
                  <Bar
                    dataKey="spend"
                    fill="var(--color-spend)"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={16}
                  />
                </BarChart>
              </ChartContainer>
            </div>
          )}

          <div data-testid="trend-chart-metric">
            <ChartContainer config={metricConfig} className="h-40 w-full">
              <LineChart data={data} margin={{ top: 4, right: 4, left: 4, bottom: 4 }}>
                <CartesianGrid vertical={false} />
                <XAxis dataKey="date" tickLine={false} axisLine={false} tick={{ fontSize: 11 }} />
                <YAxis
                  width={Y_AXIS_WIDTH}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 11 }}
                />
                <ChartTooltip
                  content={
                    <ChartTooltipContent
                      formatter={(value) => formatMetric(Number(value), 'integer')}
                    />
                  }
                />
                <Line
                  type="monotone"
                  dataKey={metric}
                  stroke={`var(--color-${metric})`}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              </LineChart>
            </ChartContainer>
          </div>
        </div>
      )}
    </div>
  );
}
